import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Bell, Bot, Server, AlertTriangle, CheckCircle, Settings, Plus, Trash2, Loader2, X, Search, Globe, RefreshCw, Monitor, Wifi, WifiOff, Clock, ChevronDown, ChevronRight, Eye, Image as ImageIcon, Crown, ArrowUpRight, Lock } from 'lucide-react';
import SubscriptionExpiredOverlay from './SubscriptionExpiredOverlay';
import UpgradeRequired from './UpgradeRequired';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';

interface NotificationBot {
  id: number;
  name: string;
  type: 'telegram' | 'discord' | 'webhook';
  config: any;
  is_active: boolean;
  created_at: string;
  created_by?: number;
  owner_username?: string;
}

interface ManagedUser {
  id: number;
  username: string;
}

interface MonitoringRule {
  id: number;
  name: string;
  type: 'server_status' | 'link_status' | 'certificate_expiry';
  servers: number[];
  check_interval: number; // minutes
  is_active: boolean;
  last_check?: string;
}

interface Notification {
  id: number;
  type: 'info' | 'warning' | 'error' | 'success' | 'activation';
  title: string;
  message: string;
  created_at: string;
  read: boolean;
}

interface MonitoringSettings {
  server_status_enabled: boolean;
  link_status_enabled: boolean;
  ssl_certificate_enabled: boolean;
  yandex_ct_enabled: boolean;
  google_ct_enabled: boolean;
}

interface BrowserCheckerStatus {
  available: boolean;
  browsers?: { activeBrowsers: number; maxBrowsers: number };
  proxies?: { total: number; active: number; available?: number; inCooldown?: number };
}

interface CheckResult {
  domain: string;
  yandex?: { blocked: boolean; details?: string; checkUrl?: string };
  google?: { blocked: boolean; details?: string; checkUrl?: string };
  availability?: { accessible: boolean; loadTime?: number; protocol?: string };
  summary?: { blocked: boolean; blockedIn: string[]; accessible: boolean };
}

interface CheckHistoryItem {
  id: number;
  domain: string;
  check_type: string;
  is_blocked: boolean;
  status_details: string;
  check_url: string;
  proxy_info?: string;
  load_time?: number;
  last_check: string;
}

interface Proxy {
  id: string;
  server: string;
  location?: string;
  enabled: boolean;
  failures?: number;
  inCooldown?: boolean;
}

interface ActiveDomain {
  id: number;
  domain: string;
  is_active: boolean;
  ssl_status?: string;
  ssl_expires_at?: string;
  last_check?: string;
  created_at: string;
}

interface DomainDetails {
  domain: string;
  screenshot?: string;
  yandex?: { blocked: boolean; status: string; details?: string; checkUrl?: string; loadTime?: number };
  google?: { blocked: boolean; status: string; details?: string; checkUrl?: string; loadTime?: number };
  availability?: { accessible: boolean; loadTime?: number; protocol?: string; httpStatus?: number; title?: string; proxyUsed?: { id: string; server: string; location?: string } };
  timings?: { dns?: number; connect?: number; ttfb?: number; domContentLoaded?: number; load?: number };
  isLoading?: boolean;
}

const NotificationsPanel: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const { canAccess, requiredPlan, isExpired } = usePermissions();
  const userRole = user?.role || 'user';
  const isPremium = ['pro', 'enterprise', 'admin'].includes(userRole);
  const canAccessBrowser = canAccess('browser');

  const [activeTab, setActiveTab] = useState<'notifications' | 'bots' | 'monitoring' | 'browser-check'>('notifications');
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [bots, setBots] = useState<NotificationBot[]>([]);
  const [monitoringRules, setMonitoringRules] = useState<MonitoringRule[]>([]);
  const [monitoringSettings, setMonitoringSettings] = useState<MonitoringSettings>({
    server_status_enabled: false,
    link_status_enabled: false,
    ssl_certificate_enabled: false,
    yandex_ct_enabled: false,
    google_ct_enabled: false
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Bot modal state
  const [showBotModal, setShowBotModal] = useState(false);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [showBrowserUpgradeModal, setShowBrowserUpgradeModal] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<ManagedUser[]>([]);
  const [selectedManagedUserId, setSelectedManagedUserId] = useState<number | ''>('');
  const [botFormData, setBotFormData] = useState({
    name: '',
    type: 'telegram' as 'telegram' | 'discord' | 'webhook',
    config: { token: '', chatIds: '' }
  });
  const [selectedBotUserId, setSelectedBotUserId] = useState<number | ''>('');

  // Browser Checker state
  const [browserCheckerStatus, setBrowserCheckerStatus] = useState<BrowserCheckerStatus>({ available: false });



  const [checkHistory, setCheckHistory] = useState<CheckHistoryItem[]>([]);
  const [proxies, setProxies] = useState<Proxy[]>([]);
  const [showProxyModal, setShowProxyModal] = useState(false);
  const [newProxyString, setNewProxyString] = useState(''); // format: user:pass@host:port
  const [bulkProxyText, setBulkProxyText] = useState(''); // for multiple proxies

  // New Browser Check state

  const [activeLinks, setActiveLinks] = useState<any[]>([]);
  const [selectedDomain, setSelectedDomain] = useState<ActiveDomain | null>(null);
  const [domainDetails, setDomainDetails] = useState<DomainDetails | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  const effectiveManagedUserId = userRole === 'admin'
    ? (selectedManagedUserId || user?.id || '')
    : (user?.id || '');

  const withManagedUser = (url: string) => {
    if (userRole !== 'admin' || !effectiveManagedUserId) {
      return url;
    }

    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}userId=${effectiveManagedUserId}`;
  };


  // Block access for non-expired starters
  if (!canAccess('notifications') && !isExpired) {
    return (
      <div className="h-full relative min-h-[400px]">
        <UpgradeRequired
          feature="Notifications"
          requiredPlan={requiredPlan('notifications')}
          message="Access to notifications requires a premium plan."
        />
      </div>
    );
  }

  useEffect(() => {
    loadData();
  }, [activeTab, selectedManagedUserId]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab && ['notifications', 'bots', 'monitoring', 'browser-check'].includes(tab)) {
      setActiveTab(tab as any);
    }
  }, [location.search]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError('');

      if (userRole === 'admin' && (availableUsers.length === 0 || !selectedManagedUserId)) {
        const usersResponse = await fetch('/api/users', {
          credentials: 'include'
        });
        const usersData = await usersResponse.json();
        if (usersData.success) {
          setAvailableUsers(usersData.users || []);
          if (!selectedManagedUserId && user?.id) {
            setSelectedManagedUserId(user.id);
          }
        }
      }

      if (activeTab === 'notifications') {
        const response = await fetch(withManagedUser('/api/notifications'), {
          credentials: 'include'
        });
        const data = await response.json();
        if (data.success) {
          setNotifications(data.notifications);
        }
      } else if (activeTab === 'bots') {
        const requests: Promise<Response>[] = [
          fetch(withManagedUser('/api/notification-bots'), {
            credentials: 'include'
          })
        ];

        if (userRole === 'admin') {
          requests.push(fetch('/api/users', {
            credentials: 'include'
          }));
        }

        const [botsResponse, usersResponse] = await Promise.all(requests);
        const data = await botsResponse.json();
        if (data.success) {
          setBots(data.bots);
        }

        if (userRole === 'admin' && usersResponse) {
          const usersData = await usersResponse.json();
          if (usersData.success) {
            setAvailableUsers(usersData.users || []);
            if (!selectedManagedUserId && user?.id) {
              setSelectedManagedUserId(user.id);
            }
            if (!selectedBotUserId && user?.id) {
              setSelectedBotUserId(user.id);
            }
          }
        }
      } else if (activeTab === 'monitoring') {
        // Load monitoring settings
        const settingsResponse = await fetch(withManagedUser('/api/monitoring-rules/settings'), {
          credentials: 'include'
        });
        const settingsData = await settingsResponse.json();
        if (settingsData.success && settingsData.settings) {
          setMonitoringSettings({
            server_status_enabled: Boolean(settingsData.settings.server_status_enabled),
            link_status_enabled: Boolean(settingsData.settings.link_status_enabled),
            ssl_certificate_enabled: Boolean(settingsData.settings.ssl_certificate_enabled),
            yandex_ct_enabled: Boolean(settingsData.settings.yandex_ct_enabled),
            google_ct_enabled: Boolean(settingsData.settings.google_ct_enabled)
          });
        }

        // Load monitoring rules
        const response = await fetch(withManagedUser('/api/monitoring-rules'), {
          credentials: 'include'
        });
        const data = await response.json();
        if (data.success) {
          setMonitoringRules(data.rules);
        }
      } else if (activeTab === 'browser-check') {
        // Load browser checker status
        const statusResponse = await fetch('/api/browser-checker/status', {
          credentials: 'include'
        });
        const statusData = await statusResponse.json();
        if (statusData.success) {
          setBrowserCheckerStatus({
            available: statusData.available,
            browsers: statusData.stats?.browsers,
            proxies: statusData.stats?.proxies
          });
        }

        // Load proxies from database
        const proxiesResponse = await fetch('/api/browser-checker/proxies', {
          credentials: 'include'
        });
        const proxiesData = await proxiesResponse.json();
        if (proxiesData.success) {
          setProxies(proxiesData.proxies || []);
        }

        // Load active links from pages
        const pagesResponse = await fetch('/api/pages', {
          credentials: 'include'
        });
        const pagesData = await pagesResponse.json();
        if (pagesData.success) {
          const links: any[] = [];
          pagesData.pages.forEach((page: any) => {
            if (page.activations && page.activations.length > 0) {
              page.activations.forEach((activation: any) => {
                if (activation.status === 'active') {
                  links.push({
                    id: activation.id,
                    url: activation.activated_url,
                    domain: activation.domain_name || 'Unknown',
                    custom_path: activation.custom_path,
                    expires_at: activation.expires_at
                  });
                }
              });
            }
          });
          setActiveLinks(links);
        }

        // Load check history
        const historyResponse = await fetch('/api/browser-checker/history', {
          credentials: 'include'
        });
        const historyData = await historyResponse.json();
        if (historyData.success) {
          setCheckHistory(historyData.history || []);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleMonitoringSetting = async (setting: keyof MonitoringSettings) => {
    const newValue = !monitoringSettings[setting];
    const newSettings = {
      ...monitoringSettings,
      [setting]: newValue
    };

    // Optimistically update UI
    setMonitoringSettings(newSettings);
    setIsSavingSettings(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await fetch('/api/monitoring-rules/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          ...newSettings,
          userId: userRole === 'admin' ? effectiveManagedUserId : undefined
        })
      });

      const data = await response.json();

      if (data.success) {
        // Settings saved silently without notification
      } else {
        // Revert on error
        setMonitoringSettings(monitoringSettings);
        setError(data.error || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving monitoring settings:', error);
      // Revert on error
      setMonitoringSettings(monitoringSettings);
      setError('Failed to save settings');
    } finally {
      setIsSavingSettings(false);
    }
  };

  const markAsRead = async (notificationId: number) => {
    try {
      await fetch(withManagedUser(`/api/notifications/${notificationId}/read`), {
        method: 'POST',
        credentials: 'include'
      });
      setNotifications(notifications.map(n =>
        n.id === notificationId ? { ...n, read: true } : n
      ));
      // Trigger unread count update in parent component
      window.dispatchEvent(new CustomEvent('notificationRead'));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleAddBot = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const response = await fetch('/api/notification-bots', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          ...botFormData,
          userId: userRole === 'admin' ? selectedBotUserId || selectedManagedUserId || user?.id : undefined
        })
      });

      const data = await response.json();

      if (data.success && data.bot) {
        setBots([...(bots || []), data.bot]);
        setShowBotModal(false);
        setBotFormData({
          name: '',
          type: 'telegram',
          config: { token: '', chatIds: '' }
        });
        setSelectedBotUserId(selectedManagedUserId || user?.id || '');
        await loadData();
      } else {
        setError(data.error || 'Failed to add bot');
      }
    } catch (error) {
      console.error('Error adding bot:', error);
      setError('Failed to add bot');
    }
  };

  const handleDeleteBot = async (botId: number) => {
    if (!confirm('Are you sure you want to delete this bot?')) {
      return;
    }

    try {
      const response = await fetch(`/api/notification-bots/${botId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success) {
        setBots((bots || []).filter(bot => bot.id !== botId));
      } else {
        setError(data.error || 'Failed to delete bot');
      }
    } catch (error) {
      console.error('Error deleting bot:', error);
      setError('Failed to delete bot');
    }
  };

  const handleTestBot = async (botId: number) => {
    try {
      const response = await fetch(`/api/notification-bots/${botId}/test`, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success) {
        alert('Test message sent successfully!');
      } else {
        alert(`Test failed: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error testing bot:', error);
      alert('Failed to test bot');
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-accent-green" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-accent-yellow" />;
      case 'error':
        return <AlertTriangle className="w-5 h-5 text-accent-red" />;
      case 'activation':
        return <Globe className="w-5 h-5 text-accent-blue" />;
      default:
        return <Bell className="w-5 h-5 text-accent-green" />;
    }
  };

  const getBotIcon = (type: string) => {
    switch (type) {
      case 'telegram':
        return <Bot className="w-6 h-6 text-accent-blue" />;
      case 'discord':
        return <Bot className="w-6 h-6 text-purple-500" />;
      case 'webhook':
        return <Bot className="w-6 h-6 text-accent-green" />;
      default:
        return <Bot className="w-6 h-6 text-text-muted" />;
    }
  };

  const handleClearAllNotifications = async () => {
    if (!confirm('Are you sure you want to clear all notifications?')) {
      return;
    }

    try {
      const response = await fetch('/api/notifications/clear-all', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          userId: userRole === 'admin' ? effectiveManagedUserId : undefined
        })
      });

      const data = await response.json();

      if (data.success) {
        setNotifications([]);
        // Trigger unread count update in parent component
        window.dispatchEvent(new CustomEvent('notificationsCleared'));
      } else {
        setError(data.error || 'Failed to clear notifications');
      }
    } catch (error) {
      console.error('Error clearing notifications:', error);
      setError('Failed to clear notifications');
    }
  };



  // Parse proxy string format: user:pass@host:port or http(s)://user:pass@host:port
  const parseProxyString = (proxyStr: string) => {
    const trimmed = proxyStr.trim();

    // Detect protocol
    let protocol = 'http';
    let cleanStr = trimmed;

    if (cleanStr.startsWith('https://')) {
      protocol = 'https';
      cleanStr = cleanStr.substring(8);
    } else if (cleanStr.startsWith('http://')) {
      protocol = 'http';
      cleanStr = cleanStr.substring(7);
    }

    const atIndex = cleanStr.lastIndexOf('@');
    if (atIndex === -1) {
      // No auth, just host:port
      return { server: `${protocol}://${cleanStr}`, username: '', password: '' };
    }

    const auth = cleanStr.substring(0, atIndex);
    const hostPort = cleanStr.substring(atIndex + 1);
    const colonIndex = auth.indexOf(':');

    if (colonIndex === -1) {
      return { server: `${protocol}://${hostPort}`, username: auth, password: '' };
    }

    return {
      server: `${protocol}://${hostPort}`,
      username: auth.substring(0, colonIndex),
      password: auth.substring(colonIndex + 1)
    };
  };

  const handleAddProxy = async () => {
    if (!newProxyString.trim()) {
      setError('Proxy is required');
      return;
    }

    const parsed = parseProxyString(newProxyString);

    if (!parsed.server) {
      setError('Invalid proxy format. Use: user:pass@host:port');
      return;
    }

    try {
      const response = await fetch('/api/browser-checker/proxies', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(parsed)
      });

      const data = await response.json();

      if (data.success) {
        setNewProxyString('');
        setSuccessMessage('Proxy added successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
        loadData();
      } else {
        setError(data.error || 'Failed to add proxy');
      }
    } catch (error) {
      console.error('Error adding proxy:', error);
      setError('Failed to add proxy');
    }
  };

  const handleAddBulkProxies = async () => {
    if (!bulkProxyText.trim()) {
      setError('Please enter at least one proxy');
      return;
    }

    const lines = bulkProxyText.split('\n').filter(line => line.trim());
    const proxies = lines.map(line => parseProxyString(line)).filter(p => p.server);

    if (proxies.length === 0) {
      setError('No valid proxies found');
      return;
    }

    try {
      const response = await fetch('/api/browser-checker/proxies/load', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ proxies })
      });

      const data = await response.json();

      if (data.success) {
        setBulkProxyText('');
        setSuccessMessage(`Successfully added ${data.loaded || proxies.length} proxies`);
        setTimeout(() => setSuccessMessage(''), 3000);
        loadData();
      } else {
        setError(data.error || 'Failed to add proxies');
      }
    } catch (error) {
      console.error('Error adding bulk proxies:', error);
      setError('Failed to add proxies');
    }
  };

  const handleDeleteProxy = async (proxyId: string) => {
    try {
      await fetch(`/api/browser-checker/proxies/${proxyId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      loadData();
    } catch (error) {
      console.error('Error deleting proxy:', error);
    }
  };

  // Handle link selection and check
  const handleSelectLink = async (link: any) => {
    setSelectedDomain({ ...link, is_active: true, created_at: new Date().toISOString() } as any);
    setDomainDetails({ domain: link.domain, isLoading: true });
    setError('');

    try {
      const response = await fetch('/api/browser-checker/check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          domain: link.url,
          checks: ['yandex', 'google', 'availability'],
          useProxy: true,
          screenshot: true
        })
      });

      const data = await response.json();

      if (data.success) {
        setDomainDetails({
          domain: link.domain,
          screenshot: data.results?.availability?.screenshot,
          yandex: data.results?.yandex,
          google: data.results?.google,
          availability: data.results?.availability,
          timings: data.results?.availability?.timings,
          isLoading: false
        });
        loadData();
      } else {
        setDomainDetails({
          domain: link.domain,
          isLoading: false
        });
        setError(data.error || 'Check failed');
      }
    } catch (error) {
      console.error('Error checking link:', error);
      setDomainDetails({
        domain: link.domain,
        isLoading: false
      });
      setError('Failed to check link');
    }
  };

  const renderNotifications = () => (
    <div className="space-y-4">
      {notifications.length === 0 ? (
        <div className="text-center py-12">
          <Bell className="w-12 h-12 text-text-muted mx-auto mb-4" />
          <p className="text-text-muted">No notifications</p>
        </div>
      ) : (
        <>
          <div className="flex justify-end mb-4">
            <button
              onClick={handleClearAllNotifications}
              className="btn-secondary flex items-center space-x-2"
            >
              <Trash2 className="w-4 h-4" />
              <span>Clear All</span>
            </button>
          </div>
          <div className="space-y-4">
            {notifications.map((notification) => {
              // Determine border color based on notification type
              const getBorderColor = () => {
                if (!notification.read) {
                  switch (notification.type) {
                    case 'activation':
                      return 'border-accent-blue/50 bg-accent-blue/5';
                    case 'success':
                      return 'border-accent-green/50 bg-accent-green/5';
                    case 'warning':
                      return 'border-accent-yellow/50 bg-accent-yellow/5';
                    case 'error':
                      return 'border-accent-red/50 bg-accent-red/5';
                    default:
                      return 'border-accent-blue/50 bg-accent-blue/5';
                  }
                }
                return '';
              };



              return (
                <div
                  key={notification.id}
                  className={`card p-4 cursor-pointer transition-colors ${getBorderColor()}`}
                  onClick={() => !notification.read && markAsRead(notification.id)}
                >
                  <div className="flex items-start space-x-3">
                    {getNotificationIcon(notification.type)}
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-text-primary">{notification.title}</h4>
                        <span className="text-xs text-text-muted">
                          {new Date(notification.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm text-text-muted mt-1">{notification.message}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );

  const renderBots = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-text-primary">Notification Bot</h3>
        <button
          onClick={() => setShowBotModal(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>{(!bots || bots.length === 0) ? 'Add Bot' : 'Add Another Bot'}</span>
        </button>
      </div>

      {!bots || bots.length === 0 ? (
        <div className="card p-8 text-center">
          <Bot className="w-12 h-12 text-text-muted mx-auto mb-4" />
          <p className="text-text-muted mb-2">No notification bot configured</p>
          <p className="text-sm text-text-muted">Add a Telegram bot to receive notifications about your servers and links</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {bots && Array.isArray(bots) && bots.map((bot) => (
              <div key={bot.id} className="card p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-card-bg-secondary rounded-lg">
                      {getBotIcon(bot?.type || 'telegram')}
                    </div>
                    <div>
                      <h4 className="font-medium text-text-primary">{bot?.name || 'Unknown Bot'}</h4>
                      <p className="text-sm text-text-muted capitalize">{bot?.type || 'telegram'}</p>
                      {userRole === 'admin' && bot?.owner_username && (
                        <p className="text-xs text-text-muted">Owner: {bot.owner_username}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${bot?.is_active ? 'bg-accent-green' : 'bg-text-muted'
                      }`}></div>
                    <span className={`text-xs ${bot?.is_active ? 'text-accent-green' : 'text-text-muted'
                      }`}>
                      {bot?.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-text-muted">
                    Added {bot?.created_at ? new Date(bot.created_at).toLocaleDateString() : 'Unknown'}
                  </span>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleTestBot(bot.id)}
                      className="p-1 text-accent-green hover:bg-accent-green/10 rounded transition-colors"
                      title="Test bot connection"
                    >
                      <CheckCircle className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteBot(bot.id)}
                      className="p-1 text-accent-red hover:bg-accent-red/10 rounded transition-colors"
                      title="Delete bot"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderMonitoring = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-text-primary">Monitoring Options</h3>
        {isSavingSettings && (
          <div className="flex items-center space-x-2 text-accent-blue">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Saving...</span>
          </div>
        )}
      </div>

      {successMessage && (
        <div className="p-4 bg-accent-green/10 border border-accent-green/20 rounded-lg">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-accent-green" />
            <p className="text-accent-green font-medium">{successMessage}</p>
          </div>
        </div>
      )}

      <div className="card p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Server className="w-6 h-6 text-accent-purple" />
            <div>
              <p className="font-medium text-text-primary">Server Status Monitoring</p>
              <p className="text-sm text-text-muted">Monitor server uptime and availability</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={monitoringSettings.server_status_enabled}
              onChange={() => handleToggleMonitoringSetting('server_status_enabled')}
              disabled={isSavingSettings}
            />
            <div className="w-11 h-6 bg-table-row-dark peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-accent-green/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-border-default after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-green peer-disabled:opacity-50"></div>
          </label>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Bell className="w-6 h-6 text-accent-green" />
            <div>
              <p className="font-medium text-text-primary">Link Status Monitoring</p>
              <p className="text-sm text-text-muted">Check active links and page availability</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={monitoringSettings.link_status_enabled}
              onChange={() => handleToggleMonitoringSetting('link_status_enabled')}
              disabled={isSavingSettings}
            />
            <div className="w-11 h-6 bg-table-row-dark peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-accent-green/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-border-default after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-green peer-disabled:opacity-50"></div>
          </label>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="w-6 h-6 text-accent-yellow" />
            <div>
              <p className="font-medium text-text-primary">SSL Certificate Monitoring</p>
              <p className="text-sm text-text-muted">Monitor SSL certificate expiry dates</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={monitoringSettings.ssl_certificate_enabled}
              onChange={() => handleToggleMonitoringSetting('ssl_certificate_enabled')}
              disabled={isSavingSettings}
            />
            <div className="w-11 h-6 bg-table-row-dark peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-accent-green/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-border-default after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-green peer-disabled:opacity-50"></div>
          </label>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Search className="w-6 h-6 text-accent-red" />
            <div>
              <p className="font-medium text-text-primary">Yandex CT Notifications</p>
              <p className="text-sm text-text-muted">Monitor certificate transparency logs on Yandex</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={monitoringSettings.yandex_ct_enabled}
              onChange={() => handleToggleMonitoringSetting('yandex_ct_enabled')}
              disabled={isSavingSettings}
            />
            <div className="w-11 h-6 bg-table-row-dark peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-accent-green/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-border-default after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-green peer-disabled:opacity-50"></div>
          </label>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Globe className="w-6 h-6 text-accent-blue" />
            <div>
              <p className="font-medium text-text-primary">Google CT Notifications</p>
              <p className="text-sm text-text-muted">Monitor certificate transparency logs on Google</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={monitoringSettings.google_ct_enabled}
              onChange={() => handleToggleMonitoringSetting('google_ct_enabled')}
              disabled={isSavingSettings}
            />
            <div className="w-11 h-6 bg-table-row-dark peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-accent-green/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-border-default after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-green peer-disabled:opacity-50"></div>
          </label>
        </div>
      </div>

      {!monitoringRules || monitoringRules.length === 0 ? (
        <div className="text-center py-12">
        </div>
      ) : (
        <div className="space-y-4">
          {monitoringRules && monitoringRules.map((rule) => (
            <div key={rule.id} className="card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-text-primary">{rule.name}</h4>
                  <p className="text-sm text-text-muted capitalize">
                    {rule.type.replace('_', ' ')} • Check every {rule.check_interval} minutes
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  <div className={`w-2 h-2 rounded-full ${rule.is_active ? 'bg-accent-green' : 'bg-text-muted'
                    }`}></div>
                  <span className={`text-xs ${rule.is_active ? 'text-accent-green' : 'text-text-muted'
                    }`}>
                    {rule.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderBrowserCheck = () => (
    <div className="w-full min-h-full bg-gradient-to-br from-background to-card-bg/30 -m-6 md:-m-8 p-6 md:p-8">
      <div className="mx-auto max-w-7xl px-6 lg:px-12 w-full">
        <div className="grid items-stretch gap-8 lg:grid-cols-2 w-full">
          {/* Left Column - Domain List */}
          <section className="space-y-4 flex flex-col">

            {/* Active Links List */}
            <div className="rounded-3xl bg-card-bg/95 shadow-xl backdrop-blur-sm overflow-hidden">
              <div className="border-b border-border-default/30 px-6 py-4">
                <h2 className="text-lg font-semibold text-text-primary">Active Links</h2>
                <p className="mt-1 text-xs text-text-muted">{activeLinks.length} links available for checking</p>
              </div>

              <div className="max-h-[400px] overflow-y-auto">
                {activeLinks.length === 0 ? (
                  <div className="text-center py-12 px-6">
                    <Globe className="w-12 h-12 text-text-muted mx-auto mb-4" />
                    <p className="text-text-muted">No active links</p>
                  </div>
                ) : (
                  <div className="divide-y divide-border-default/30">
                    {activeLinks.map((link) => (
                      <button
                        key={link.id}
                        onClick={() => handleSelectLink(link)}
                        className={`w-full text-left px-6 py-4 hover:bg-table-row-hover transition-colors ${selectedDomain?.id === link.id ? 'bg-accent-blue/10 border-l-2 border-accent-blue' : ''
                          }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-2 h-2 rounded-full bg-accent-green" />
                            <div>
                              <p className="font-medium text-text-primary">{link.domain}</p>
                              <p className="text-xs text-text-muted truncate max-w-[250px]">{link.url}</p>
                            </div>
                          </div>
                          {selectedDomain?.id === link.id && domainDetails?.isLoading && (
                            <Loader2 className="w-4 h-4 animate-spin text-accent-blue" />
                          )}
                          {selectedDomain?.id === link.id && !domainDetails?.isLoading && (
                            <Eye className="w-4 h-4 text-accent-blue" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Collapsible History */}
            <div className="rounded-2xl bg-card-bg/50 shadow-sm overflow-hidden">
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="w-full flex items-center justify-between px-6 py-4 hover:bg-table-row-hover transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <Clock className="w-5 h-5 text-text-muted" />
                  <span className="font-medium text-text-primary">Check History</span>
                  <span className="text-xs text-text-muted">({checkHistory.length})</span>
                </div>
                {showHistory ? (
                  <ChevronDown className="w-5 h-5 text-text-muted" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-text-muted" />
                )}
              </button>

              {showHistory && (
                <div className="border-t border-border-default/30 max-h-[200px] overflow-y-auto">
                  {checkHistory.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-text-muted text-sm">No check history</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-border-default/30">
                      {checkHistory.slice(0, 20).map((item) => {
                        // Parse proxy info if available
                        let proxyData: { id?: string; server?: string; location?: string } | null = null;
                        if (item.proxy_info) {
                          try {
                            proxyData = JSON.parse(item.proxy_info);
                          } catch (e) {
                            // ignore parse errors
                          }
                        }

                        return (
                          <div key={item.id} className="px-6 py-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium text-text-primary">{item.domain}</p>
                                <p className="text-xs text-text-muted capitalize">{item.check_type}</p>
                              </div>
                              <div className="text-right">
                                <span className={`px-2 py-1 rounded text-xs ${item.is_blocked ? 'bg-accent-red/20 text-accent-red' : 'bg-accent-green/20 text-accent-green'}`}>
                                  {item.is_blocked ? 'Blocked' : 'Clean'}
                                </span>
                                <p className="text-xs text-text-muted mt-1">{new Date(item.last_check).toLocaleString()}</p>
                              </div>
                            </div>
                            {/* Proxy and timing info */}
                            <div className="flex items-center justify-between mt-2 text-xs">
                              {proxyData ? (
                                <span className="text-accent-blue flex items-center space-x-1">
                                  <Globe className="w-3 h-3" />
                                  <span>{proxyData.location || proxyData.server || 'Proxy'}</span>
                                </span>
                              ) : (
                                <span className="text-text-muted">Direct</span>
                              )}
                              {item.load_time && (
                                <span className="text-text-muted">{item.load_time}ms</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* Right Column - Domain Details */}
          <section className="flex flex-col">
            <div className="rounded-3xl bg-card-bg/95 shadow-2xl backdrop-blur-sm flex-1">
              {!selectedDomain ? (
                <div className="p-12 text-center">
                  <ImageIcon className="w-16 h-16 text-text-muted mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-text-primary mb-2">Select a domain</h3>
                  <p className="text-sm text-text-muted">Click on a domain on the left to check and view screenshot</p>
                </div>
              ) : domainDetails?.isLoading ? (
                <div className="p-12 text-center">
                  <Loader2 className="w-12 h-12 animate-spin text-accent-blue mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-text-primary mb-2">Checking {selectedDomain.domain}</h3>
                  <p className="text-sm text-text-muted">Please wait...</p>
                </div>
              ) : domainDetails ? (
                <>
                  {/* Header */}
                  <div className="border-b border-border-default/30 px-8 py-6">
                    <h2 className="text-xl font-semibold text-text-primary">{domainDetails.domain}</h2>
                    <p className="mt-1 text-sm text-text-muted">Результаты проверки</p>
                  </div>

                  {/* Screenshot */}
                  {domainDetails.screenshot && (
                    <div className="px-8 py-6 border-b border-border-default/30">
                      <h3 className="text-sm font-semibold uppercase tracking-wider text-text-muted mb-4">Скриншот</h3>
                      <div className="rounded-xl overflow-hidden border border-border-default">
                        <img
                          src={`data:image/png;base64,${domainDetails.screenshot}`}
                          alt={`Screenshot of ${domainDetails.domain}`}
                          className="w-full h-auto"
                        />
                      </div>
                    </div>
                  )}

                  {/* CT Status */}
                  <div className="px-8 py-6 border-b border-border-default/30">
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-text-muted mb-4">CT Status</h3>
                    <div className="grid grid-cols-2 gap-4">
                      {/* Yandex */}
                      <div className={`p-4 rounded-xl border ${!domainDetails.yandex || domainDetails.yandex.blocked === null || domainDetails.yandex.blocked === undefined
                        ? 'border-accent-yellow bg-accent-yellow/10'
                        : domainDetails.yandex.blocked
                          ? 'border-accent-red bg-accent-red/10'
                          : 'border-accent-green bg-accent-green/10'
                        }`}>
                        <div className="flex items-center space-x-2 mb-2">
                          <Search className={`w-4 h-4 ${!domainDetails.yandex || domainDetails.yandex.blocked === null || domainDetails.yandex.blocked === undefined
                            ? 'text-accent-yellow'
                            : domainDetails.yandex.blocked
                              ? 'text-accent-red'
                              : 'text-accent-green'
                            }`} />
                          <span className="font-medium text-sm">Yandex</span>
                        </div>
                        <p className={`text-lg font-bold ${!domainDetails.yandex || domainDetails.yandex.blocked === null || domainDetails.yandex.blocked === undefined
                          ? 'text-accent-yellow'
                          : domainDetails.yandex.blocked
                            ? 'text-accent-red'
                            : 'text-accent-green'
                          }`}>
                          {!domainDetails.yandex || domainDetails.yandex.blocked === null || domainDetails.yandex.blocked === undefined
                            ? 'Недоступно'
                            : domainDetails.yandex.blocked
                              ? 'КТ'
                              : 'Чисто'}
                        </p>
                        {domainDetails.yandex?.loadTime && (
                          <p className="text-xs text-text-muted mt-1">{domainDetails.yandex.loadTime}ms</p>
                        )}
                      </div>

                      {/* Google */}
                      <div className={`p-4 rounded-xl border ${!domainDetails.google || domainDetails.google.blocked === null || domainDetails.google.blocked === undefined
                        ? 'border-accent-yellow bg-accent-yellow/10'
                        : domainDetails.google.blocked
                          ? 'border-accent-red bg-accent-red/10'
                          : 'border-accent-green bg-accent-green/10'
                        }`}>
                        <div className="flex items-center space-x-2 mb-2">
                          <Globe className={`w-4 h-4 ${!domainDetails.google || domainDetails.google.blocked === null || domainDetails.google.blocked === undefined
                            ? 'text-accent-yellow'
                            : domainDetails.google.blocked
                              ? 'text-accent-red'
                              : 'text-accent-green'
                            }`} />
                          <span className="font-medium text-sm">Google</span>
                        </div>
                        <p className={`text-lg font-bold ${!domainDetails.google || domainDetails.google.blocked === null || domainDetails.google.blocked === undefined
                          ? 'text-accent-yellow'
                          : domainDetails.google.blocked
                            ? 'text-accent-red'
                            : 'text-accent-green'
                          }`}>
                          {!domainDetails.google || domainDetails.google.blocked === null || domainDetails.google.blocked === undefined
                            ? 'Недоступно'
                            : domainDetails.google.blocked
                              ? 'КТ'
                              : 'Чисто'}
                        </p>
                        {domainDetails.google?.loadTime && (
                          <p className="text-xs text-text-muted mt-1">{domainDetails.google.loadTime}ms</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Availability & Timings */}
                  {domainDetails.availability && (
                    <div className="px-8 py-6">
                      <h3 className="text-sm font-semibold uppercase tracking-wider text-text-muted mb-4">Доступность</h3>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-text-muted">Статус</span>
                          <span className={`font-medium ${domainDetails.availability.accessible ? 'text-accent-green' : 'text-accent-red'}`}>
                            {domainDetails.availability.accessible ? 'Доступен' : 'Недоступен'}
                          </span>
                        </div>
                        {domainDetails.availability.protocol && (
                          <div className="flex items-center justify-between">
                            <span className="text-text-muted">Протокол</span>
                            <span className="text-text-primary font-medium">{domainDetails.availability.protocol.toUpperCase()}</span>
                          </div>
                        )}
                        {domainDetails.availability.httpStatus && (
                          <div className="flex items-center justify-between">
                            <span className="text-text-muted">HTTP Status</span>
                            <span className="text-text-primary font-medium">{domainDetails.availability.httpStatus}</span>
                          </div>
                        )}
                        {domainDetails.availability.loadTime && (
                          <div className="flex items-center justify-between">
                            <span className="text-text-muted">Время загрузки</span>
                            <span className="text-text-primary font-medium">{domainDetails.availability.loadTime}ms</span>
                          </div>
                        )}
                        {domainDetails.availability.title && (
                          <div className="flex items-center justify-between">
                            <span className="text-text-muted">Title</span>
                            <span className="text-text-primary font-medium text-right max-w-[200px] truncate">{domainDetails.availability.title}</span>
                          </div>
                        )}
                        {domainDetails.availability.proxyUsed && (
                          <div className="flex items-center justify-between">
                            <span className="text-text-muted">Прокси</span>
                            <span className="text-text-primary font-medium text-right text-xs">
                              {domainDetails.availability.proxyUsed.location || 'Unknown'}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Timing details */}
                      {domainDetails.timings && (
                        <div className="mt-4 pt-4 border-t border-border-default/30">
                          <h4 className="text-xs font-semibold uppercase tracking-wider text-text-muted mb-3">Timing (TTL)</h4>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            {domainDetails.timings.dns !== undefined && (
                              <div className="flex justify-between">
                                <span className="text-text-muted">DNS</span>
                                <span className="text-text-primary">{domainDetails.timings.dns}ms</span>
                              </div>
                            )}
                            {domainDetails.timings.connect !== undefined && (
                              <div className="flex justify-between">
                                <span className="text-text-muted">Connect</span>
                                <span className="text-text-primary">{domainDetails.timings.connect}ms</span>
                              </div>
                            )}
                            {domainDetails.timings.ttfb !== undefined && (
                              <div className="flex justify-between">
                                <span className="text-text-muted">TTFB</span>
                                <span className="text-text-primary">{domainDetails.timings.ttfb}ms</span>
                              </div>
                            )}
                            {domainDetails.timings.domContentLoaded !== undefined && (
                              <div className="flex justify-between">
                                <span className="text-text-muted">DOM</span>
                                <span className="text-text-primary">{domainDetails.timings.domContentLoaded}ms</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Recheck button */}
                  <div className="px-8 py-6 border-t border-border-default/30">
                    <button
                      onClick={() => {
                        const link = activeLinks.find(l => l.id === selectedDomain?.id);
                        if (link) handleSelectLink(link);
                      }}
                      className="btn-primary w-full flex items-center justify-center gap-3 rounded-2xl py-4 text-base font-semibold"
                    >
                      <RefreshCw className="w-5 h-5" />
                      <span>Проверить снова</span>
                    </button>
                  </div>
                </>
              ) : null}
            </div>

            {/* Browser Service - показывать только когда нет результатов */}
            {!selectedDomain && (
              <button
                onClick={() => setShowProxyModal(true)}
                className="mt-8 w-full rounded-2xl bg-card-bg/50 p-4 shadow-sm hover:bg-table-row-hover transition-colors flex items-center justify-between"
              >
                <div className="flex items-center space-x-3">
                  {browserCheckerStatus.available ? (
                    <Wifi className="w-5 h-5 text-accent-green" />
                  ) : (
                    <WifiOff className="w-5 h-5 text-accent-red" />
                  )}
                  <div className="text-left">
                    <p className="font-medium text-text-primary text-sm">Browser Service</p>
                    <p className={`text-xs ${browserCheckerStatus.available ? 'text-accent-green' : 'text-accent-red'}`}>
                      {browserCheckerStatus.available ? 'Online' : 'Offline'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-xs text-text-muted text-right">
                    <p>Proxies: {browserCheckerStatus.proxies?.active || 0}/{browserCheckerStatus.proxies?.total || 0}</p>
                    <p>Browsers: {browserCheckerStatus.browsers?.activeBrowsers || 0}/{browserCheckerStatus.browsers?.maxBrowsers || 3}</p>
                  </div>
                  <Settings className="w-4 h-4 text-accent-blue" />
                </div>
              </button>
            )}
          </section>
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full min-h-full animate-fade-in">
      <div className="flex gap-8">
        {/* Sidebar Navigation */}
        <div className="w-56 flex-shrink-0">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-white">Notifications</h1>
            <p className="text-sm text-white/40 mt-1">Manage alerts and monitoring</p>
          </div>

          {/* Navigation Groups */}
          <nav className="space-y-6">
            {/* Notifications Group */}
            <div>
              <p className="text-xs font-medium text-white/30 uppercase tracking-wider mb-2 px-3">Notifications</p>
              <div className="space-y-1">
                <button
                  onClick={() => setActiveTab('notifications')}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${activeTab === 'notifications'
                    ? 'bg-white/10 text-white font-medium'
                    : 'text-white/50 hover:text-white hover:bg-white/5'
                    }`}
                >
                  <Bell className="w-4 h-4" />
                  Notifications
                </button>
                {isPremium && (
                  <button
                    onClick={() => setActiveTab('bots')}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${activeTab === 'bots'
                      ? 'bg-white/10 text-white font-medium'
                      : 'text-white/50 hover:text-white hover:bg-white/5'
                      }`}
                  >
                    <Bot className="w-4 h-4" />
                    Bots
                  </button>
                )}
              </div>
            </div>

            {/* Monitoring Group */}
            {isPremium && (
              <div>
                <p className="text-xs font-medium text-white/30 uppercase tracking-wider mb-2 px-3">Monitoring</p>
                <div className="space-y-1">
                  <button
                    onClick={() => setActiveTab('monitoring')}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${activeTab === 'monitoring'
                      ? 'bg-white/10 text-white font-medium'
                      : 'text-white/50 hover:text-white hover:bg-white/5'
                      }`}
                  >
                    <Settings className="w-4 h-4" />
                    Monitoring
                  </button>
                  <button
                    onClick={() => canAccessBrowser ? setActiveTab('browser-check') : setShowBrowserUpgradeModal(true)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${activeTab === 'browser-check'
                      ? 'bg-white/10 text-white font-medium'
                      : canAccessBrowser
                        ? 'text-white/50 hover:text-white hover:bg-white/5'
                        : 'text-white/30 cursor-not-allowed'
                      }`}
                  >
                    <Monitor className="w-4 h-4" />
                    Browser
                    {!canAccessBrowser && <Lock className="w-3 h-3 ml-auto text-white/30" />}
                  </button>
                </div>
              </div>
            )}
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          {userRole === 'admin' && activeTab !== 'browser-check' && availableUsers.length > 0 && (
            <div className="mb-6 flex items-center gap-3">
              <label className="text-sm font-medium text-white/60 whitespace-nowrap">
                Active user
              </label>
              <select
                className="input-field max-w-sm"
                value={selectedManagedUserId}
                onChange={(e) => {
                  const nextUserId = Number(e.target.value);
                  setSelectedManagedUserId(nextUserId);
                  setSelectedBotUserId(nextUserId);
                }}
              >
                {availableUsers.map((managedUser) => (
                  <option key={managedUser.id} value={managedUser.id}>
                    {managedUser.username} (ID: {managedUser.id})
                  </option>
                ))}
              </select>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <span className="text-red-400">{error}</span>
            </div>
          )}

          {/* Content */}
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center space-y-4">
                <Loader2 className="w-8 h-8 text-white/40 animate-spin mx-auto" />
                <p className="text-white/40 text-sm">Loading...</p>
              </div>
            </div>
          ) : (
            <>
              {activeTab === 'notifications' && (
                <div className="relative">
                  <SubscriptionExpiredOverlay visible={isExpired} featureName="Notifications" />
                  <div className={isExpired ? 'opacity-20 blur-sm pointer-events-none' : ''}>
                    {renderNotifications()}
                  </div>
                </div>
              )}
              {activeTab === 'bots' && renderBots()}
              {activeTab === 'monitoring' && (
                <div className="relative">
                  <SubscriptionExpiredOverlay visible={isExpired} featureName="Monitoring" />
                  <div className={isExpired ? 'opacity-20 blur-sm pointer-events-none' : ''}>
                    {renderMonitoring()}
                  </div>
                </div>
              )}
              {activeTab === 'browser-check' && (
                (canAccessBrowser || isExpired) ? (
                  <div className="relative">
                    <SubscriptionExpiredOverlay visible={isExpired} featureName="Browser Service" />
                    <div className={isExpired ? 'opacity-20 blur-sm pointer-events-none' : ''}>
                      {renderBrowserCheck()}
                    </div>
                  </div>
                ) : (
                  <div className="rounded-lg bg-white/[0.02] border border-white/10 p-12 text-center relative overflow-hidden">
                    <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-white/5 flex items-center justify-center">
                      <Lock className="w-8 h-8 text-white/40" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">Browser Check Locked</h3>
                    <p className="text-white/50 text-sm mb-6">
                      Browser Check requires {requiredPlan('browser')} plan to access.
                    </p>
                    <a
                      href="https://ikcore.cloud/pricing"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-black rounded-lg text-sm font-medium hover:bg-white/90 transition-colors"
                    >
                      <Crown className="w-4 h-4" />
                      Upgrade to Enterprise
                      <ArrowUpRight className="w-4 h-4" />
                    </a>
                  </div>
                )
              )}
            </>
          )}
        </div>
      </div>

      {/* Add Bot Modal */}
      {showBotModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card-bg border border-border-default rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-text-primary">Add Notification Bot</h3>
              <button
                onClick={() => setShowBotModal(false)}
                className="p-2 hover:bg-table-row-hover rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-text-muted" />
              </button>
            </div>

            <form onSubmit={handleAddBot} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">
                  Bot Name
                </label>
                <input
                  type="text"
                  className="input-field w-full"
                  placeholder="My Telegram Bot"
                  value={botFormData.name}
                  onChange={(e) => setBotFormData({ ...botFormData, name: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">
                  Telegram Bot Token
                </label>
                <input
                  type="text"
                  className="input-field w-full"
                  placeholder="1234567890:ABCdefGHIjklMNOpqrsTUVwxyz"
                  value={botFormData.config.token || ''}
                  onChange={(e) => setBotFormData({
                    ...botFormData,
                    config: { ...botFormData.config, token: e.target.value }
                  })}
                  required
                />
                <p className="text-xs text-text-muted mt-1">
                  Get your bot token from @BotFather on Telegram
                </p>
              </div>

              {userRole === 'admin' && (
                <div>
                  <label className="block text-sm font-semibold text-text-primary mb-2">
                    Assign to User
                  </label>
                  <select
                    className="input-field w-full"
                    value={selectedBotUserId}
                    onChange={(e) => setSelectedBotUserId(Number(e.target.value))}
                    required
                  >
                    <option value="" disabled>Select user</option>
                    {availableUsers.map((managedUser) => (
                      <option key={managedUser.id} value={managedUser.id}>
                        {managedUser.username} (ID: {managedUser.id})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">
                  Chat IDs (one per line)
                </label>
                <textarea
                  className="input-field w-full"
                  placeholder="123456789&#10;987654321&#10;555666777"
                  rows={4}
                  value={botFormData.config.chatIds || ''}
                  onChange={(e) => setBotFormData({
                    ...botFormData,
                    config: { ...botFormData.config, chatIds: e.target.value }
                  })}
                  required
                />
                <p className="text-xs text-text-muted mt-1">
                  Enter multiple Telegram chat IDs, one per line
                </p>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowBotModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary flex-1">
                  Add Bot
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Rule Modal */}
      {showRuleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card-bg border border-border-default rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-text-primary">Add Monitoring Rule</h3>
              <button
                onClick={() => setShowRuleModal(false)}
                className="p-2 hover:bg-table-row-hover rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-text-muted" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">
                  Rule Name
                </label>
                <input
                  type="text"
                  className="input-field w-full"
                  placeholder="Server Health Check"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">
                  Rule Type
                </label>
                <select className="input-field w-full">
                  <option value="server_health">Server Health</option>
                  <option value="link_status">Link Status</option>
                  <option value="ssl_expiry">SSL Certificate Expiry</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">
                  Check Interval (minutes)
                </label>
                <input
                  type="number"
                  className="input-field w-full"
                  placeholder="30"
                  min="5"
                  max="1440"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => setShowRuleModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button className="btn-primary flex-1">
                  Add Rule
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Proxy Settings Modal - Improved Design */}
      {showProxyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card-bg border border-border-default rounded-xl w-full max-w-2xl mx-4 max-h-[85vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-border-default">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent-purple/20 to-accent-blue/20 flex items-center justify-center">
                  <Globe className="w-5 h-5 text-accent-purple" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-text-primary">Proxy Manager</h3>
                  <p className="text-xs text-text-muted">
                    {proxies.length} прокси • {proxies.filter(p => p.enabled && !p.inCooldown).length} активных
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowProxyModal(false)}
                className="p-2 hover:bg-table-row-hover rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-text-muted" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {/* Success Message */}
              {successMessage && (
                <div className="p-3 bg-accent-green/10 border border-accent-green/20 rounded-lg text-accent-green text-sm flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>{successMessage}</span>
                </div>
              )}

              {/* Current Proxies List */}
              {proxies.length > 0 && (
                <div className="rounded-xl border border-border-default overflow-hidden">
                  <div className="bg-table-row-dark px-4 py-3 border-b border-border-default">
                    <h4 className="text-sm font-semibold text-text-primary flex items-center space-x-2">
                      <span>Активные прокси</span>
                      <span className="px-2 py-0.5 bg-accent-purple/10 text-accent-purple rounded-full text-xs">
                        {proxies.length}
                      </span>
                    </h4>
                  </div>
                  <div className="max-h-[200px] overflow-y-auto divide-y divide-border-default/50">
                    {proxies.map((proxy) => {
                      let displayServer = proxy.server || 'Unknown';
                      try {
                        if (displayServer.startsWith('http://')) {
                          displayServer = displayServer.substring(7);
                        }
                      } catch (e) { }

                      const isActive = proxy.enabled && !proxy.inCooldown;
                      return (
                        <div key={proxy.id} className="flex items-center justify-between px-4 py-3 hover:bg-table-row-hover/50 transition-colors">
                          <div className="flex items-center space-x-3">
                            <div className={`w-2.5 h-2.5 rounded-full ${isActive ? 'bg-accent-green animate-pulse' : 'bg-accent-red'}`} />
                            <div>
                              <p className="text-sm font-medium text-text-primary">{displayServer}</p>
                              <div className="flex items-center space-x-2 text-xs text-text-muted">
                                <span>{proxy.location || 'Unknown'}</span>
                                {(proxy.failures ?? 0) > 0 && (
                                  <span className="text-accent-red">• {proxy.failures} ошибок</span>
                                )}
                                {proxy.inCooldown && (
                                  <span className="text-accent-yellow">• Cooldown</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteProxy(proxy.id)}
                            className="p-2 text-text-muted hover:text-accent-red hover:bg-accent-red/10 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Add Proxy Section */}
              <div className="space-y-4">
                {/* Single Proxy */}
                <div className="p-4 rounded-xl border border-border-default bg-table-bg">
                  <label className="block text-sm font-semibold text-text-primary mb-2">
                    Добавить прокси
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      className="input-field flex-1"
                      placeholder="user:pass@host:port или host:port"
                      value={newProxyString}
                      onChange={(e) => setNewProxyString(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddProxy()}
                    />
                    <button
                      onClick={handleAddProxy}
                      className="btn-primary px-4"
                      disabled={!newProxyString.trim()}
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Bulk Add */}
                <div className="p-4 rounded-xl border border-border-default bg-table-bg">
                  <label className="block text-sm font-semibold text-text-primary mb-2">
                    Массовое добавление
                  </label>
                  <textarea
                    className="input-field w-full resize-none"
                    placeholder="Один прокси на строку:&#10;user:pass@host:port&#10;user2:pass2@host2:port2&#10;host3:port3"
                    rows={4}
                    value={bulkProxyText}
                    onChange={(e) => setBulkProxyText(e.target.value)}
                  />
                  <button
                    onClick={handleAddBulkProxies}
                    className="btn-primary w-full mt-3"
                    disabled={!bulkProxyText.trim()}
                  >
                    Добавить {bulkProxyText.trim().split('\n').filter(l => l.trim()).length || 0} прокси
                  </button>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-border-default bg-table-row-dark">
              <button
                onClick={() => {
                  setShowProxyModal(false);
                  setNewProxyString('');
                  setBulkProxyText('');
                  setSuccessMessage('');
                }}
                className="btn-secondary w-full"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Browser Upgrade Modal */}
      {showBrowserUpgradeModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowBrowserUpgradeModal(false)}>
          <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-8 max-w-sm w-full mx-4 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-white/5 flex items-center justify-center">
              <Lock className="w-8 h-8 text-white/40" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Browser Check Locked</h3>
            <p className="text-white/50 text-sm mb-6">
              Browser Check requires Enterprise plan to access.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowBrowserUpgradeModal(false)}
                className="flex-1 py-2.5 text-white/60 hover:text-white border border-white/10 rounded-lg hover:border-white/20 transition-colors text-sm"
              >
                Cancel
              </button>
              <a
                href="https://ikcore.cloud/pricing"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 py-2.5 bg-white text-black rounded-lg text-sm font-medium hover:bg-white/90 transition-colors flex items-center justify-center gap-2"
              >
                <Crown className="w-4 h-4" />
                Upgrade
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsPanel;
